import * as React from "react";
import type { SlashCommandItem } from "../../utils/collaborateSlashCommands";

export type CollaborateSlashListProps = {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
};

export type CollaborateSlashListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

const CollaborateSlashList = React.forwardRef<CollaborateSlashListRef, CollaborateSlashListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    React.useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = React.useCallback(
      (index: number) => {
        const item = items[index];
        if (item) {
          command(item);
        }
      },
      [command, items]
    );

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === "ArrowUp") {
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="collaborate-mention-popover">
          <div className="collaborate-mention-popover__empty">No commands</div>
        </div>
      );
    }

    return (
      <div className="collaborate-mention-popover" role="listbox" aria-label="Slash commands">
        <div className="collaborate-mention-popover__heading">Commands</div>
        {items.map((item, index) => {
          const active = index === selectedIndex;
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={active}
              className={`collaborate-mention-popover__item${active ? " collaborate-mention-popover__item--active" : ""}`}
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(index);
              }}
            >
              <span className="collaborate-mention-popover__slash-icon" aria-hidden>
                /
              </span>
              <span className="collaborate-mention-popover__meta">
                <span className="collaborate-mention-popover__name">{item.label}</span>
                <span className="collaborate-mention-popover__email">{item.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }
);

CollaborateSlashList.displayName = "CollaborateSlashList";

export default CollaborateSlashList;
